import React, { useEffect, useRef, useState } from "react";
import { formatTime } from "../../../utilities/conversions";

interface Props {
    callId: string | undefined;                   // unique call id
    initialSeconds: number;           // callDetail.duration * 60
    alertMinutesBefore: number;       // e.g. 1
    alertBeepMessage: string;         // beep.mp3 URL
    onTimeEnd: () => void;            // callback when timer hits 0
    onBuyMinutes: (minutes: number) => void; // callback when more minutes are bought
}

const CallTimer: React.FC<Props> = ({
    callId,
    initialSeconds,
    alertMinutesBefore,
    alertBeepMessage,
    onTimeEnd,
    onBuyMinutes,
}) => {
    const [remainingSeconds, setRemainingSeconds] = useState<number>(() => {
        const saved = localStorage.getItem(`call_timer_${callId}`);
        return saved ? parseInt(saved, 10) : initialSeconds;
    });

    const [showAddMinutes, setShowAddMinutes] = useState(false);
    const alertPlayed = useRef(false);

    /** Countdown */
    useEffect(() => {
        if (remainingSeconds <= 0) {
            onTimeEnd();
            localStorage.removeItem(`call_timer_${callId}`);
            return;
        }

        localStorage.setItem(`call_timer_${callId}`, remainingSeconds.toString());

        const interval = setInterval(() => {
            setRemainingSeconds((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(interval);
    }, [remainingSeconds, callId, onTimeEnd]);

    /** Alert + modal */
    useEffect(() => {
        const minutesLeft = Math.floor(remainingSeconds / 60);
        if (
            minutesLeft === alertMinutesBefore &&
            !alertPlayed.current &&
            remainingSeconds % 60 === 0
        ) {
            const audio = new Audio(alertBeepMessage);
            audio.play().catch((e) => console.warn("Audio blocked", e));
            setShowAddMinutes(true);
            alertPlayed.current = true;
        }
    }, [remainingSeconds, alertMinutesBefore, alertBeepMessage]);

    return (
        <>
            {/* Timer overlay */}
            <div className="absolute top-2 right-2 bg-black/50 text-white font-bold px-3 py-1 rounded-lg">
                {formatTime(remainingSeconds)}
            </div>

            {/* Tailwind Modal */}
            {showAddMinutes && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                    <div className="bg-orange-400 rounded-xl p-6 w-80 text-center shadow-lg">
                        <p className="text-black font-medium">
                            This session ends in {alertMinutesBefore} minute
                            {alertMinutesBefore > 1 ? "s" : ""}. Add more time?
                        </p>

                        <div className="flex justify-around mt-4">
                            {[5, 10, 15].map((m) => (
                                <button
                                    key={m}
                                    className="bg-white text-black px-4 py-2 rounded-full font-semibold shadow hover:bg-gray-100"
                                    onClick={() => {
                                        onBuyMinutes(m);
                                        setRemainingSeconds((prev) => prev + m * 60);
                                        localStorage.setItem(
                                            `call_timer_${callId}`,
                                            (remainingSeconds + m * 60).toString()
                                        );
                                        alertPlayed.current = false;
                                        setShowAddMinutes(false);
                                    }}
                                >
                                    {m} Min
                                </button>
                            ))}
                        </div>

                        <button
                            className="mt-4 bg-red-500 text-white px-6 py-2 rounded-full font-semibold hover:bg-red-600"
                            onClick={() => setShowAddMinutes(false)}
                        >
                            No thanks
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default CallTimer;
